import { clamp, isNull } from 'lodash-es'
import { zeroIfNaN } from './bits'

import envRaw from './env-raw'
import setCss from './set-css'
import pageScroll from './page-scroll'
import smoothScroll from './smooth-scroll'
import viewportResize from './viewport-resize'

import { fmtTranslate } from './fmt-translate'

const TOP = 'top'
const LEFT = 'left'
const HEIGHT = 'height'
const OPACITY = 'opacity'
const DISPLAY = 'display'
const TRANSFORM = 'transform'
const TRANSITION = 'transition'

const SCALE_ZERO = 'scale(0.2)'
const OPACITY_ZERO = 0
const DISPLAY_NONE = 'none'

const SILENT_STR = 'silent'
const ANIMATE_STR = 'animate'

export default function MyIsotopeMasonry(
  transitionDuration,
  isoGridContentElm,
  descDiffPairs,
  highlightPeriod,
  castToX1,
  castToX2,
  getMasonrySizerWidth,
  getMasonryWidth) {

  const transitionAnimate = `opacity ${transitionDuration}ms, transform ${transitionDuration}ms`

  const scrollSpeed = envRaw.masonryScrollSpeed // px/ms
  const scrollEaseFunc = envRaw.masonryScrollEaseFunc

  const colList = []
  const pairList = []
  const postList = []

  let animate = true

  let skippedX2 = false
  let lastColIndexX2 = null
  let prePositionCount = 0
  let highlightedCount = 0

  let sizerWidth = null
  let masonryWidth = null

  const pendingMovePairs = []
  const pendingShowPairs = []

  let pendingMoveTimeout = null
  let pendingShowTimeout = null

  setCss(isoGridContentElm, HEIGHT, null)

  resetupBaseValues()

  return {
    pairs,
    contains,
    scrollTo,
    silentMode,
    animateMode,
    currentMode,
    appendSilent,
    removeSilent,
    removeVirtual,

    destroy,

    arrange,
    prepend,
    resize,
    append,
    remove
  }

  // mains

  function pairs() {
    return pairList.concat().sort(descDiffPairs)
  }

  function destroy() {
    pairList.length = 0
    postList.length = 0

    setCss(isoGridContentElm, HEIGHT, null)
  }

  function contains(post) {
    return postList.indexOf(post) !== -1
  }

  function scrollTo(post) {
    const postIndex = postList.indexOf(post)
    if (postIndex === -1) return

    const pair = pairList[postIndex]
    if (!pair.coord || !pair.size) return

    if (calcIsInViewport(pair)) return
    if (calcIsFillViewport(pair)) return

    let scrollTop = null
    if (calcMasonryHeight() > viewportResize.getHeight()) {
      const tolerance = Math.round(viewportResize.getHeight() / 5)
      const minScrollTop = Math.round(Math.max(0, calcMasonryOffset() - tolerance))
      const maxScrollTop = Math.round(calcMasonryOffset() + calcMasonryHeight() - viewportResize.getHeight() + tolerance)
      const naiveScrollTop = Math.round(calcMasonryOffset() + pair.coord.y + pair.size.previewHeight / 2 - viewportResize.getHeight() / 2)
      scrollTop = clamp(naiveScrollTop, minScrollTop, maxScrollTop)
    } else {
      scrollTop = Math.round((calcMasonryOffset() + calcMasonryHeight() / 2) - viewportResize.getHeight() / 2)
    }

    const scrollDuration = calcScrollDuration(pageScroll.getTop(), scrollTop)
    smoothScroll.top(document.documentElement, scrollTop, scrollDuration, scrollEaseFunc)
  }

  function silentMode() {
    animate = false
  }

  function animateMode() {
    animate = true
  }

  function currentMode() {
    return animate
      ? ANIMATE_STR
      : SILENT_STR
  }

  function arrange() {
    postList.length = 0
    pairList.sort(descDiffPairs)

    resetupBaseValues()
    for (let index = 0, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      postList.push(pair.post)
      const fromCoord = pair.coord
      pair.coord = virtuallyPlacePair(pair)
      prepareToMovePair(pair, fromCoord)
      deferMovePairs(pair)
    }
    adjustContentHeight()
  }

  function prepend(pairs) {
    resetupBaseValues()

    for (let index = pairs.length - 1; index !== -1; index--) {
      const pair = pairs[index]
      pairList.unshift(pair)
      postList.unshift(pair.post)
    }

    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      const coord = virtuallyPlacePair(pair)
      pair.coord = coord
      actuallyPlacePair(pair, coord)
      deferShowPairs(pair)
    }

    for (let index = pairs.length, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      const fromCoord = pair.coord
      pair.coord = virtuallyPlacePair(pair)
      prepareToMovePair(pair, fromCoord)
      deferMovePairs(pair)
    }

    adjustContentHeight()
  }

  function resize() {
    resetupBaseValues()
    for (let index = 0, length = pairList.length; index !== length; index++) {
      const pair = pairList[index]
      pair.coord = virtuallyPlacePair(pair)
      actuallyMoveSilentPair(pair)
    }
    adjustContentHeight()
  }

  function append(pairs) {
    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      pairList.push(pair)
      postList.push(pair.post)
      pair.coord = virtuallyPlacePair(pair)

      if (animate) {
        actuallyPlacePair(pair)
        deferShowPairs(pair)
      } else {
        actuallyPlaceAndShow(pair)
      }
    }

    adjustContentHeight()
  }

  function appendSilent(pairs) {
    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      pairList.push(pair)
      postList.push(pair.post)
      pair.coord = virtuallyPlacePair(pair)
      actuallyPlaceAndShow(pair)
    }

    adjustContentHeight()
  }

  function removeSilent(posts) {
    for (let index = 0, length = posts.length; index !== length; index++) {
      const post = posts[index]
      cancelMovePair(post)
      cancelShowPair(post)
      const postIndex = postList.indexOf(post)
      if (postIndex === -1) {
        continue
      }

      const pair = pairList[postIndex]
      pairList.splice(postIndex, 1)
      postList.splice(postIndex, 1)
      actuallyNoneDisplayPair(pair)
    }
  }

  function removeVirtual(posts) {
    for (let index = 0, length = posts.length; index !== length; index++) {
      const post = posts[index]
      cancelMovePair(post)
      cancelShowPair(post)
      const postIndex = postList.indexOf(post)
      if (postIndex === -1) {
        return
      }

      pairList.splice(postIndex, 1)
      postList.splice(postIndex, 1)
    }
  }

  function remove(posts) {
    for (let index = 0, length = posts.length; index !== length; index++) {
      const post = posts[index]
      cancelMovePair(post)
      cancelShowPair(post)
      const postIndex = postList.indexOf(post)
      if (postIndex === -1) {
        continue
      }

      const pair = pairList[postIndex]
      pairList.splice(postIndex, 1)
      postList.splice(postIndex, 1)
      actuallyHidePair(pair)
    }
  }

  // helpers

  function calcMasonryHeight() {
    return getMax(colList)
  }

  function calcMasonryOffset() {
    return isoGridContentElm.getBoundingClientRect().top + pageScroll.getTop()
  }

  function calcScrollDuration(posA, posB) {
    return Math.round(zeroIfNaN(Math.abs(posA - posB) / scrollSpeed))
  }

  function calcIsFillViewport(pair) {
    const pairEdge = calcMasonryOffset() + pair.coord.y + pair.size.previewHeight
    const pairTop = calcMasonryOffset() + pair.coord.y

    const beforeTop = pairTop <= pageScroll.getTop()
    const afterBottom = pairEdge >= pageScroll.getTop() + viewportResize.getHeight()
    const fillViewport = beforeTop && afterBottom
    return fillViewport
  }

  function calcIsInViewport(pair) {
    const pairEdge = calcMasonryOffset() + pair.coord.y + pair.size.previewHeight
    const pairTop = calcMasonryOffset() + pair.coord.y

    const tolerance = -1 * viewportResize.getHeight() / 10
    const afterTop = pairTop >= pageScroll.getTop() - tolerance
    const beforeBottom = pairEdge <= pageScroll.getTop() + viewportResize.getHeight() + tolerance
    const isInViewport = afterTop && beforeBottom
    return isInViewport
  }

  function resetupBaseValues() {
    // reset animate flag
    animate = true
    // highlight flags
    skippedX2 = false
    lastColIndexX2 = null
    prePositionCount = 0
    highlightedCount = 0
    // widths
    sizerWidth = Math.max(1, getMasonrySizerWidth())
    masonryWidth = Math.max(1, getMasonryWidth())
    // colList
    colList.length = 0
    const colNum = Math.max(1, Math.round(zeroIfNaN(masonryWidth / sizerWidth)))

    for (let index = 0; index !== colNum; index++) {
      colList[index] = 0
    }
  }

  function adjustContentHeight() {
    const maxColY = calcMasonryHeight()
    setCss(isoGridContentElm, HEIGHT, maxColY)
  }

  function prepareToMovePair(pair, fromCoord) {
    const moveCss = fmtTranslate(
      fromCoord.x - pair.coord.x,
      fromCoord.y - pair.coord.y)

    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, moveCss)
    setCss(pair.elm, OPACITY, null)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function deferMovePairs(pair) {
    pendingMovePairs.push(pair)
    if (isNull(pendingMoveTimeout)) {
      pendingMoveTimeout = setTimeout(actuallyMovePairs, 33)
    }
  }

  function cancelMovePair(post) {
    for (let index = pendingMovePairs.length - 1; index !== -1; index--) {
      const pair = pendingMovePairs[index]
      if (pair.post === post) {
        pendingMovePairs.splice(index, 1)
        break
      }
    }
  }

  function actuallyMovePairs() {
    const pairs = pendingMovePairs.concat()
    pendingMovePairs.length = 0
    pendingMoveTimeout = null
    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      setCss(pair.elm, DISPLAY, null)
      setCss(pair.elm, TRANSITION, transitionAnimate)
      setCss(pair.elm, TRANSFORM, null)
      setCss(pair.elm, OPACITY, null)
    }
  }

  function actuallyMoveSilentPair(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, null)
    setCss(pair.elm, OPACITY, null)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function actuallyPlaceAndShow(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, null)
    setCss(pair.elm, OPACITY, null)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function actuallyPlacePair(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, SCALE_ZERO)
    setCss(pair.elm, OPACITY, OPACITY_ZERO)
    setCss(pair.elm, TOP, pair.coord.y)
    setCss(pair.elm, LEFT, pair.coord.x)
  }

  function deferShowPairs(pair) {
    pendingShowPairs.push(pair)
    if (isNull(pendingShowTimeout)) {
      pendingShowTimeout = setTimeout(actuallyShowPairs, 33)
    }
  }

  function cancelShowPair(post) {
    for (let index = pendingShowPairs.length - 1; index !== -1; index--) {
      const pair = pendingShowPairs[index]
      if (pair.post === post) {
        pendingShowPairs.splice(index, 1)
        break
      }
    }
  }

  function actuallyShowPairs() {
    const pairs = pendingShowPairs.concat()
    pendingShowPairs.length = 0
    pendingShowTimeout = null
    for (let index = 0, length = pairs.length; index !== length; index++) {
      const pair = pairs[index]
      setCss(pair.elm, DISPLAY, null)
      setCss(pair.elm, TRANSITION, transitionAnimate)
      setCss(pair.elm, TRANSFORM, null)
      setCss(pair.elm, OPACITY, null)
    }
  }

  function actuallyNoneDisplayPair(pair) {
    setCss(pair.elm, DISPLAY, DISPLAY_NONE)
    setCss(pair.elm, TRANSITION, null)
    setCss(pair.elm, TRANSFORM, null)
    setCss(pair.elm, OPACITY, null)
  }

  function actuallyHidePair(pair) {
    setCss(pair.elm, DISPLAY, null)
    setCss(pair.elm, TRANSITION, transitionAnimate)
    setCss(pair.elm, TRANSFORM, SCALE_ZERO)
    setCss(pair.elm, OPACITY, OPACITY_ZERO)
  }

  function virtuallyPlacePair(pair) {
    prePositionCount++
    const colIndex = getMinIndex(colList)

    const hasRoomForX2 = true
      && colList.length !== (colIndex + 1)
      && colList[colIndex] === colList[colIndex + 1]

    const needHighlight = true
      && colList.length >= 2
      && hasRoomForX2
      && calcNeedHighlight()

    const willHighlight = true
      && needHighlight
      && ((lastColIndexX2 !== colIndex) || (colList.length === 2) || (skippedX2 === true))

    if (needHighlight) {
      skippedX2 = !willHighlight
    }

    pair.size = willHighlight
      ? castToX2(pair)
      : castToX1(pair)

    const rawSpan = Math.max(1, Math.round(zeroIfNaN(pair.size.previewWidth / sizerWidth)))
    const itemSpan = Math.min(colList.length, rawSpan)

    const highlighted = true
      && willHighlight
      && itemSpan === 2

    if (highlighted) {
      lastColIndexX2 = colIndex
      highlightedCount++
    }

    const oldColY = colList[colIndex]
    const newColY = oldColY + pair.size.previewHeight
    const groupEdge = colIndex + itemSpan

    for (let groupIndex = colIndex; groupIndex !== groupEdge; groupIndex++) {
      colList[groupIndex] = newColY
    }

    return {
      y: oldColY,
      x: sizerWidth * colIndex
    }
  }

  function calcNeedHighlight() {
    const highlight_period = highlightPeriod()
    if (highlight_period === null || highlight_period < 2) {
      return false
    }

    const offset = colList.length <= 4
      ? highlight_period - 1
      : highlight_period - 3
    const count = prePositionCount + offset
    const expHighlighted = Math.floor(zeroIfNaN(count / highlight_period))

    return expHighlighted > highlightedCount
  }
}

function getMinIndex(arr) {
  if (arr.length === 0) {
    return 0
  }

  let min = arr[0]
  let res = 0
  for (let index = 1, length = arr.length; index !== length; index++) {
    const val = arr[index]
    if (val < min) {
      min = val
      res = index
    }
  }
  return res
}

function getMax(arr) {
  if (arr.length === 0) {
    return 0
  }

  let max = arr[0]
  for (let index = 1, length = arr.length; index !== length; index++) {
    const val = arr[index]
    if (val > max) {
      max = val
    }
  }
  return max
}
